import React, { useState } from 'react';
import { getApiUrl } from '../config';
import { User, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Login = ({ onSwitchToRegister, isRegister = false, onForgotPassword, onShowPrivacyPolicy, onShowTrustSecurity }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreements, setAgreements] = useState({
    privacyPolicy: false,
    trustSecurity: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const endpoint = isRegister ? '/register' : '/login';
      const payload = isRegister 
        ? { username: formData.username, email: formData.email, password: formData.password }
        : { username: formData.username, password: formData.password };

      const response = await fetch(getApiUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log('🔧 Login response:', { status: response.status, ok: response.ok, data });

      if (response.ok) {
        console.log('🔧 Login successful, navigating to dashboard');
        setSuccess(isRegister ? 'Registration successful! Please check your email to verify your account.' : 'Login successful!');
        
        // Navigate to dashboard after successful login
        setTimeout(() => {
          navigate('/console');
        }, 1000);
      } else {
        console.log('🔧 Login failed:', data.error);
        setError(data.error || 'An error occurred');
      }
    } catch (err) {
      console.error('🔧 Login error:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAgreementChange = (agreement) => {
    setAgreements(prev => ({
      ...prev,
      [agreement]: !prev[agreement]
    }));
  };

  const validateForm = () => {
    if (!formData.username || !formData.password) return false;
    if (isRegister) {
      if (!formData.email) return false;
      if (formData.password !== formData.confirmPassword) return false;
      if (formData.password.length < 6) return false;
      if (!agreements.privacyPolicy || !agreements.trustSecurity) return false;
    }
    return true;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            {isRegister ? 'Create your account' : 'Sign in to your account'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-300">
            {isRegister ? 'Create your account to get started' : 'Sign in to your account'}
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-white">
                Username
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className="appearance-none relative block w-full px-3 py-2 pl-10 border border-gray-700 bg-gray-900 placeholder-gray-500 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm transition-colors"
                  placeholder="Enter your username"
                  value={formData.username}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {isRegister && (
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white">
                  Email
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="appearance-none relative block w-full px-3 py-2 pl-10 border border-gray-700 bg-gray-900 placeholder-gray-500 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm transition-colors"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="appearance-none relative block w-full px-3 py-2 pl-10 pr-10 border border-gray-700 bg-gray-900 placeholder-gray-500 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm transition-colors"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {isRegister && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-white">
                  Confirm Password
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    className="appearance-none relative block w-full px-3 py-2 pl-10 pr-10 border border-gray-700 bg-gray-900 placeholder-gray-500 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm transition-colors"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Privacy Policy and Trust & Security Agreements */}
            {isRegister && (
              <div className="space-y-3 pt-2">
                <div className="flex items-start space-x-3">
                  <div className="flex items-center h-5">
                    <button
                      type="button"
                      onClick={() => handleAgreementChange('privacyPolicy')}
                      className={`w-4 h-4 border-2 rounded flex items-center justify-center transition-colors ${
                        agreements.privacyPolicy 
                          ? 'border-purple-500 bg-purple-500' 
                          : 'border-gray-600 bg-gray-900'
                      }`}
                    >
                      {agreements.privacyPolicy && (
                        <CheckCircle className="h-3 w-3 text-white" />
                      )}
                    </button>
                  </div>
                  <div className="text-sm">
                    <label className="text-gray-300 cursor-pointer">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={onShowPrivacyPolicy}
                        className="text-purple-400 hover:text-purple-300 underline transition-colors"
                      >
                        Privacy Policy
                      </button>
                    </label>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex items-center h-5">
                    <button
                      type="button"
                      onClick={() => handleAgreementChange('trustSecurity')}
                      className={`w-4 h-4 border-2 rounded flex items-center justify-center transition-colors ${
                        agreements.trustSecurity 
                          ? 'border-purple-500 bg-purple-500' 
                          : 'border-gray-600 bg-gray-900'
                      }`}
                    >
                      {agreements.trustSecurity && (
                        <CheckCircle className="h-3 w-3 text-white" />
                      )}
                    </button>
                  </div>
                  <div className="text-sm">
                    <label className="text-gray-300 cursor-pointer">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={onShowTrustSecurity}
                        className="text-purple-400 hover:text-purple-300 underline transition-colors"
                      >
                        Trust & Security
                      </button>
                      {' '}policies
                    </label>
                  </div>
                </div>

                <div className="flex items-center space-x-2 text-xs text-gray-400">
                  <Shield className="h-4 w-4" />
                  <span>Your data is protected with industry-leading security practices</span>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-700/50 text-red-300 px-4 py-3 rounded-md text-sm backdrop-blur-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-900/20 border border-green-700/50 text-green-300 px-4 py-3 rounded-md text-sm backdrop-blur-sm">
              {success}
            </div>
          )}

          {!isRegister && (
            <div className="text-right">
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
              >
                Forgot your password?
              </button>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={!validateForm() || isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                isRegister ? 'Create Account' : 'Sign In'
              )}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={onSwitchToRegister}
              className="text-purple-400 hover:text-purple-300 text-sm transition-colors"
            >
              {isRegister 
                ? 'Already have an account? Sign in' 
                : "Don't have an account? Sign up"
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login; 